import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import type { IClientUser } from '../../interfaces/user';
import Navigation from '../../components/Navigation';
import { isAdmin } from '../../utils/adminAccess';
import { GET_ALL_USERS } from '../../graphql/GET_ALL_USERS';
import { TOGGLE_USER_ACCESS } from '../../graphql/TOGGLE_USER_ACCESS';
import userIcon from '../../assets/svg/user-icon.svg';
import searchIcon from '../../assets/svg/search.svg';

const AdminUsersPage: React.FC<IClientUser> = (props) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [dateFilter, setDateFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const usersPerPage = 10;

    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { data, loading, error, refetch } = useQuery(GET_ALL_USERS);
    const users = data?.getAllUsers.filter((user: any) => !isAdmin(user)) || [];

    const [toggleUserAccess] = useMutation(TOGGLE_USER_ACCESS, {
        onCompleted: () => {
            refetch();
            setLoadingUserId(null);
            setOpenDropdownId(null);
        },
        onError: (error) => {
            console.error('Error toggling user access:', error);
            alert(`Error: ${error.message}`);
            setLoadingUserId(null);
        }
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, dateFilter, sortBy]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const filterByDate = (userList: any[]) => {
        if (dateFilter === 'all') return userList;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        return userList.filter((user: any) => {
            if (!user.createdAt) return false;
            const userDate = new Date(user.createdAt);

            switch (dateFilter) {
                case 'today':
                    return userDate >= today;
                case 'last7days': {
                    const sevenDaysAgo = new Date(today);
                    sevenDaysAgo.setDate(today.getDate() - 7);
                    return userDate >= sevenDaysAgo;
                }
                case 'last30days': {
                    const thirtyDaysAgo = new Date(today);
                    thirtyDaysAgo.setDate(today.getDate() - 30);
                    return userDate >= thirtyDaysAgo;
                }
                default:
                    return true;
            }
        });
    };

    const sortUsers = (userList: any[]) => {
        const sorted = [...userList];

        switch (sortBy) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.createdAt).getTime();
                    const dateB = new Date(b.createdAt).getTime();
                    return dateB - dateA;
                });
            case 'oldest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.createdAt).getTime();
                    const dateB = new Date(b.createdAt).getTime();
                    return dateA - dateB;
                });
            case 'name-asc':
                return sorted.sort((a, b) => {
                    const nameA = (a.profile?.name || a.email).toLowerCase();
                    const nameB = (b.profile?.name || b.email).toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    let filteredUsers = users.filter((user: any) => {
        const searchLower = searchTerm.toLowerCase();
        const name = user.profile?.name?.toLowerCase() || '';
        const email = user.email?.toLowerCase() || '';
        return name.includes(searchLower) || email.includes(searchLower);
    });

    filteredUsers = filterByDate(filteredUsers);
    filteredUsers = sortUsers(filteredUsers);

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getInitials = (name: string) => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const currentUsers = filteredUsers.slice(startIndex, endIndex);

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const getPageNumbers = () => {
        const pages: number[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = 4;
            }

            if (currentPage >= totalPages - 2) {
                startPage = totalPages - 3;
            }

            if (startPage > 2) {
                pages.push(-1);
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) {
                pages.push(-2);
            }

            pages.push(totalPages);
        }

        return pages;
    };

    const getUserTypeBadge = (userType: string | null) => {
        if (!userType) return 'Creator';
        return userType.charAt(0).toUpperCase() + userType.slice(1).toLowerCase();
    };

    const getUserTypeBadgeClass = (userType: string | null) => {
        const type = userType?.toUpperCase();
        if (type === 'BRAND') return 'user-type-badge brand';
        return 'user-type-badge creator';
    };

    const handleToggleAccess = async (userId: string, currentBlockedStatus: boolean) => {
        setLoadingUserId(userId);
        try {
            await toggleUserAccess({
                variables: {
                    userId: parseInt(userId),
                    isBlocked: !currentBlockedStatus
                }
            });
        } catch (error) {
        }
    };

    const toggleDropdown = (userId: string) => {
        setOpenDropdownId(openDropdownId === userId ? null : userId);
    };

    return (
        <main className="page-admin-users">
            <Fragment>
                <Navigation {...props} />
                <section className="container">
                    <div className="admin-users-container">

                        {/* Header Section */}
                        <div className="admin-header">
                            <div className="admin-header-content">
                                <h1 className="admin-title">Users</h1>
                                <p className="admin-subtitle">View all registered users</p>
                            </div>
                        </div>

                        {!loading && !error && (
                            <div className="admin-stats-card">
                                <div className="stat-card">
                                    <div className="stat-icon-wrapper">
                                        <img src={userIcon} alt="Users" />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Users</div>
                                        <div className="stat-value">{users.length}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Filter Bar */}
                        {!loading && !error && (
                            <div className="admin-filters">
                                <div className="filters-left">
                                    <div className="filter-group">
                                        <label className="filter-label">Registration Date:</label>
                                        <div className="select">
                                            <select
                                                value={dateFilter}
                                                onChange={(e) => setDateFilter(e.target.value)}
                                            >
                                                <option value="all">All Time</option>
                                                <option value="today">Today</option>
                                                <option value="last7days">Last 7 Days</option>
                                                <option value="last30days">Last 30 Days</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="filter-group">
                                        <label className="filter-label">Sort By:</label>
                                        <div className="select">
                                            <select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                            >
                                                <option value="newest">Newest First</option>
                                                <option value="oldest">Oldest First</option>
                                                <option value="name-asc">Name (A-Z)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {(dateFilter !== 'all' || sortBy !== 'newest') && (
                                    <button
                                        className="button clear-filters-btn"
                                        onClick={() => {
                                            setDateFilter('all');
                                            setSortBy('newest');
                                        }}
                                    >
                                        <span>Clear Filters</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Search Bar */}
                        <div className="admin-search-container">
                            <div className="control has-icons-left">
                                <input
                                    className="input admin-search-input"
                                    type="text"
                                    placeholder="Search by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <span className="icon is-left search-icon">
                                    <img src={searchIcon} alt="Search" />
                                </span>
                            </div>
                        </div>

                        {/* Loading State */}
                        {loading && (
                            <div className="admin-loading">
                                <div className="loading-spinner"></div>
                                <p>Loading users...</p>
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="admin-error">
                                <i className="fas fa-exclamation-circle"></i>
                                <p>Error loading users: {error.message}</p>
                            </div>
                        )}

                        {/* Users Table */}
                        {!loading && !error && (
                            <>
                                {filteredUsers.length > 0 ? (
                                    <>
                                        {/* Search Results Info */}
                                        {searchTerm && (
                                            <div className="search-results-info">
                                                <i className="fas fa-filter"></i>
                                                Showing {filteredUsers.length} of {users.length} users matching "{searchTerm}"
                                            </div>
                                        )}

                                        <div className="table-container">
                                            <table className="table is-fullwidth is-hoverable">
                                                <thead>
                                                    <tr>
                                                        <th className="table-number-col">#</th>
                                                        <th>Name</th>
                                                        <th>Type</th>
                                                        <th>Email</th>
                                                        <th>Registration Date</th>
                                                        <th className="table-actions-col">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentUsers.map((user, index) => (
                                                        <tr key={user.id}>
                                                            <td className="table-number-col">{startIndex + index + 1}</td>
                                                            <td className="table-name-col">
                                                                <div className="user-name-cell">
                                                                    <div className="user-avatar-small">
                                                                        {user.avatar?.fileUrl ? (
                                                                            <img src={user.avatar.fileUrl} alt={user.profile?.name} />
                                                                        ) : (
                                                                            <span>{getInitials(user.profile?.name || user.email)}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="user-info-wrapper">
                                                                        <div className="user-name-row">
                                                                            <span>{user.profile?.name || 'Unnamed User'}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={getUserTypeBadgeClass(user.userType)}>
                                                                    {getUserTypeBadge(user.userType)}
                                                                </span>
                                                            </td>
                                                            <td className="table-email-col">{user.email}</td>
                                                            <td>{formatDate(user.createdAt)}</td>
                                                            <td className="table-actions-col">
                                                                <div
                                                                    className={`actions-dropdown-wrapper ${openDropdownId === user.id ? 'is-open' : ''}`}
                                                                    ref={openDropdownId === user.id ? dropdownRef : null}
                                                                >
                                                                    <button
                                                                        className={`status-dropdown-button ${user.isBlocked ? 'blocked' : 'active'}`}
                                                                        onClick={() => toggleDropdown(user.id)}
                                                                        disabled={loadingUserId === user.id}
                                                                    >
                                                                        {loadingUserId === user.id ? (
                                                                            <span className="dropdown-loading-spinner"></span>
                                                                        ) : (
                                                                            <>
                                                                                <span className="status-indicator">
                                                                                    <span className="status-dot"></span>
                                                                                    <span className="status-text">{user.isBlocked ? 'Blocked' : 'Active'}</span>
                                                                                </span>
                                                                                <i className="fas fa-chevron-down"></i>
                                                                            </>
                                                                        )}
                                                                    </button>

                                                                    {openDropdownId === user.id && (
                                                                        <div className="status-dropdown-menu">
                                                                            <button
                                                                                className={`status-option ${user.isBlocked ? 'enable' : 'block'}`}
                                                                                onClick={async () => await handleToggleAccess(user.id, user.isBlocked)}
                                                                            >
                                                                                {user.isBlocked ? (
                                                                                    <>
                                                                                        <i className="fas fa-check-circle"></i>
                                                                                        <span>Enable Access</span>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <i className="fas fa-ban"></i>
                                                                                        <span>Block Access</span>
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Pagination Info and Controls */}
                                        <div className="pagination-wrapper">
                                            <div className="pagination-info">
                                                Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                                            </div>

                                            {totalPages > 1 && (
                                                <div className="pagination-controls">
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={prevPage}
                                                        disabled={currentPage === 1}
                                                    >
                                                        <i className="fas fa-chevron-left"></i>
                                                        Previous
                                                    </button>

                                                    <div className="pagination-numbers">
                                                        {getPageNumbers().map((page, index) => {
                                                            if (page < 0) {
                                                                return (
                                                                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                                                                        ...
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <button
                                                                    key={page}
                                                                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                                                    onClick={() => goToPage(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    <button
                                                        className="pagination-btn"
                                                        onClick={nextPage}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                        <i className="fas fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <div className="admin-empty">
                                        <i className="fas fa-users"></i>
                                        <p className="empty-title">No users found</p>
                                        <p className="empty-subtitle">
                                            {searchTerm
                                                ? `No users match "${searchTerm}"`
                                                : 'No registered users yet'}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </section>
            </Fragment>
        </main>
    );
};

export default AdminUsersPage;